import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {AddressDto} from '@sportngin/se-commerce-sdk';
import {Observable, takeUntil} from 'rxjs';
import {NavigationEventService} from '../../../add-ons/navigation-event.service';
import {BreakpointUtilityService} from '../../../common/service';
import {OnDestroySubject} from '../../../data/model';
import * as addressActions from '../../address.actions';
import * as addressSelector from '../../address.selectors';

interface SuggestedAddressRadioForm {
  useSuggested: FormControl<boolean | null>;
}

@Component({
  selector: 'app-suggested-address',
  templateUrl: './suggested-address.component.html',
  styleUrls: ['./suggested-address.component.scss'],
  host: {class: 'app-suggested-address'},
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SuggestedAddressComponent
  extends OnDestroySubject
  implements OnInit
{
  @ViewChild('radioErrorMessage') radioErrorEle: ElementRef;
  suggestedAddress$: Observable<AddressDto | null>;
  originalAddress$: Observable<AddressDto | null>;
  verifiedAddressRequired$: Observable<boolean>;
  continueClicked = false;
  mustUseSuggestedAddress = false;
  ready = false;
  routedFlow: boolean;
  suggestedAddressForm: FormGroup<SuggestedAddressRadioForm> = new FormGroup({
    useSuggested: new FormControl<boolean | null>(null, [Validators.required]),
  });

  constructor(
    protected breakpointUtils: BreakpointUtilityService,
    private store: Store,
    private continueEvent: NavigationEventService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.suggestedAddress$ = this.store.select(
      addressSelector.selectSuggestedAddress,
    );
    this.originalAddress$ = this.store.select(
      addressSelector.selectOriginalAddress,
    );
    this.verifiedAddressRequired$ = this.store.select(
      addressSelector.selectVerifiedAddressRequired,
    );
    this.routedFlow = true;
  }

  ngOnInit(): void {
    this.ready = true;
    this.subscribeToAddressSelection();
    this.continueEvent.continueClick
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.continueClicked = true;
        this.continueToCheckout();
      });
  }

  formHasErrors(): boolean {
    return (
      this.suggestedAddressForm.errors?.['required'] && this.continueClicked
    );
  }

  subscribeToAddressSelection(): void {
    this.suggestedAddressForm.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(value => {
        this.store.dispatch(
          addressActions.selectOriginalAddress({
            originalSelected: !value.useSuggested,
          }),
        );
      });

    this.verifiedAddressRequired$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(verifiedAddressRequired => {
        this.mustUseSuggestedAddress = verifiedAddressRequired;

        if (this.mustUseSuggestedAddress) {
          this.suggestedAddressForm.controls.useSuggested.setValue(true, {
            emitEvent: false,
          });
          this.store.dispatch(
            addressActions.selectOriginalAddress({originalSelected: false}),
          );
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  continueToCheckout(): void {
    if (this.suggestedAddressForm.valid) {
      this.completeOrder();
    }
  }

  protected completeOrder(): void {
    this.store.dispatch(addressActions.createAddress());
  }
}
